import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import {
  ApiUnreachableError,
  NotSubscribedError,
  UnauthenticatedError,
} from '@/api/client'
import { AuthProvider } from '@/auth/AuthProvider'
import { applyTheme } from '@/lib/theme'
import { routeTree } from './routeTree.gen'
import '@/styles/globals.css'

// Set the theme before first paint to avoid a flash of the wrong scheme.
const stored = localStorage.getItem('ttdb-theme')
applyTheme(
  stored === 'light'
    ? 'light'
    : stored === 'dark'
      ? 'dark'
      : window.matchMedia('(prefers-color-scheme: light)').matches
        ? 'light'
        : 'dark',
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retrying an auth or entitlement failure is pointless and just delays
      // the redirect the user needs.
      retry: (failureCount, error) => {
        if (error instanceof UnauthenticatedError) return false
        if (error instanceof NotSubscribedError) return false
        // One retry is worth it in case the backend is still starting; more
        // just delays a message the user needs to see.
        if (error instanceof ApiUnreachableError) return failureCount < 1
        return failureCount < 2
      },
      refetchOnWindowFocus: false,
    },
  },
})

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const el = document.getElementById('root')
if (!el) throw new Error('#root not found')

createRoot(el).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
)
